// Master-mix export (docs/04 §4.10) — real-time capture. A Tone.Recorder taps
// the master bus; we play N loop passes, optionally fade the tail, then convert
// the recording to a portable WAV and download it.

import * as Tone from "tone";

export type ExportFormat = "wav" | "mp3";

export interface CaptureOptions {
  loops: number;
  loopDurSec: number;
  fade: boolean;
  onProgress?: (p: number) => void;
}

const FADE_SEC = 0.6;

export class MixRecorder {
  private readonly recorder: Tone.Recorder;

  constructor(master: Tone.ToneAudioNode) {
    this.recorder = new Tone.Recorder();
    master.connect(this.recorder);
  }

  /**
   * Record the live master output for `loops` passes. `fadeRamp(sec)` is called
   * FADE_SEC before the end so the caller can fade the master out.
   */
  async capture(opts: CaptureOptions, fadeRamp?: (sec: number) => void): Promise<Blob> {
    await this.recorder.start();
    const totalMs = Math.max(100, opts.loops * opts.loopDurSec * 1000);
    const startedAt = performance.now();
    let fadeStarted = false;

    await new Promise<void>((resolve) => {
      const tick = (): void => {
        const elapsed = performance.now() - startedAt;
        opts.onProgress?.(Math.min(1, elapsed / totalMs));
        if (opts.fade && fadeRamp && !fadeStarted && elapsed >= totalMs - FADE_SEC * 1000) {
          fadeStarted = true;
          fadeRamp(FADE_SEC);
        }
        if (elapsed >= totalMs) resolve();
        else requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });

    return this.recorder.stop();
  }

  dispose(): void {
    this.recorder.dispose();
  }
}

/** Decode a recorded blob and re-encode it as a 16-bit PCM WAV (portable). */
export async function blobToWav(blob: Blob): Promise<Blob> {
  const arrayBuf = await blob.arrayBuffer();
  const ctx = Tone.getContext().rawContext as unknown as AudioContext;
  const audio = await ctx.decodeAudioData(arrayBuf);
  return encodeWav(audio);
}

/** Decode a recorded blob and re-encode it as the requested format. */
export async function encodeRecording(blob: Blob, format: ExportFormat): Promise<Blob> {
  const arrayBuf = await blob.arrayBuffer();
  const ctx = Tone.getContext().rawContext as unknown as AudioContext;
  const audio = await ctx.decodeAudioData(arrayBuf);
  return format === "mp3" ? encodeMp3(audio) : encodeWav(audio);
}

export const formatExt = (f: ExportFormat): string => (f === "mp3" ? "mp3" : "wav");

function floatToInt16(f: Float32Array): Int16Array {
  const out = new Int16Array(f.length);
  for (let i = 0; i < f.length; i++) {
    const s = Math.max(-1, Math.min(1, f[i] ?? 0));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** Encode an AudioBuffer to MP3 (192 kbps, mono or stereo). lamejs is loaded
 * on demand so it stays out of the main bundle until you export MP3. */
async function encodeMp3(buffer: AudioBuffer, kbps = 192): Promise<Blob> {
  const { Mp3Encoder } = await import("@breezystack/lamejs");
  const numCh = Math.min(2, buffer.numberOfChannels);
  const enc = new Mp3Encoder(numCh, buffer.sampleRate, kbps);
  const left = floatToInt16(buffer.getChannelData(0));
  const right = numCh > 1 ? floatToInt16(buffer.getChannelData(1)) : left;
  const block = 1152;
  const chunks: Uint8Array[] = [];
  for (let i = 0; i < left.length; i += block) {
    const l = left.subarray(i, i + block);
    const r = right.subarray(i, i + block);
    const mp3 = numCh > 1 ? enc.encodeBuffer(l, r) : enc.encodeBuffer(l);
    if (mp3.length > 0) chunks.push(new Uint8Array(mp3));
  }
  const tail = enc.flush();
  if (tail.length > 0) chunks.push(new Uint8Array(tail));
  return new Blob(chunks as BlobPart[], { type: "audio/mpeg" });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function encodeWav(buffer: AudioBuffer): Blob {
  const numCh = buffer.numberOfChannels;
  const len = buffer.length;
  const sampleRate = buffer.sampleRate;
  const bytesPerSample = 2;
  const blockAlign = numCh * bytesPerSample;
  const dataSize = len * blockAlign;
  const out = new ArrayBuffer(44 + dataSize);
  const view = new DataView(out);

  const writeStr = (offset: number, s: string): void => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };

  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numCh, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 8 * bytesPerSample, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let c = 0; c < numCh; c++) channels.push(buffer.getChannelData(c));

  let offset = 44;
  for (let i = 0; i < len; i++) {
    for (let c = 0; c < numCh; c++) {
      const sample = Math.max(-1, Math.min(1, channels[c]![i] ?? 0));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }
  return new Blob([out], { type: "audio/wav" });
}
